import { useEffect } from "react";

// Per-page title, description and canonical URL (AK-252). index.html holds the
// site-wide defaults; each page swaps them while it's shown and restores them after.
const SITE_URL = "https://akedo.gg";
const MAX_DESCRIPTION = 160;

const headTag = (selector, create) => {
	let tag = document.head.querySelector(selector);
	if (!tag) {
		tag = create();
		document.head.appendChild(tag);
	}
	return tag;
};
const metaTag = (attribute, name) =>
	headTag(`meta[${attribute}="${name}"]`, () => {
		const tag = document.createElement("meta");
		tag.setAttribute(attribute, name);
		return tag;
	});
// Only pages that set their own meta get a canonical link; no site-wide default,
// so a page never points Google at the home page by mistake.
const setCanonical = (url) => {
	const tag = document.head.querySelector('link[rel="canonical"]');
	if (!url) return tag?.remove();
	headTag('link[rel="canonical"]', () => {
		const link = document.createElement("link");
		link.rel = "canonical";
		return link;
	}).href = url;
};

const readDefaults = () => ({
	title: document.title,
	description: metaTag("name", "description").content,
	ogTitle: metaTag("property", "og:title").content,
	ogDescription: metaTag("property", "og:description").content,
	ogUrl: metaTag("property", "og:url").content,
	canonical: null,
});
let defaults = null;

const apply = ({ title, description, ogTitle, ogDescription, ogUrl, canonical }) => {
	document.title = title;
	metaTag("name", "description").content = description;
	metaTag("property", "og:title").content = ogTitle;
	metaTag("property", "og:description").content = ogDescription;
	metaTag("property", "og:url").content = ogUrl;
	setCanonical(canonical);
};

const shorten = (text) => {
	const clean = String(text || "").replace(/\s+/g, " ").trim();
	return clean.length > MAX_DESCRIPTION ? `${clean.slice(0, MAX_DESCRIPTION - 1).replace(/\s+\S*$/, "")}…` : clean;
};

// usePageMeta({ title, description, path }); skipped until `title` is known.
export const usePageMeta = ({ title, description, path }) => {
	useEffect(() => {
		if (!title) return undefined;
		if (!defaults) defaults = readDefaults();
		const url = `${SITE_URL}${path || "/"}`;
		const text = shorten(description) || defaults.description;
		apply({ title, description: text, ogTitle: title, ogDescription: text, ogUrl: url, canonical: url });
		return () => apply(defaults);
	}, [title, description, path]);
};
